import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import fallbackTrainingsData from '../../data/trainings.json';

const LEVEL_COLORS = {
    Beginner: '#69ff47',
    Intermediate: '#00e5ff',
    Advanced: '#FFD700',
};

export default function TrainingsMain() {
    const [page, setPage] = useState(fallbackTrainingsData.page);
    const [trainings, setTrainings] = useState(fallbackTrainingsData.trainings);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [tRes, pRes] = await Promise.all([
                    supabase.from('trainings').select('slug, data').order('created_at', { ascending: true }),
                    supabase.from('trainings_page_content').select('data').eq('page_name', 'trainings-page').single()
                ]);

                if (pRes.data && !pRes.error) {
                    setPage({ ...fallbackTrainingsData.page, ...pRes.data.data });
                }

                if (tRes.data && !tRes.error && tRes.data.length > 0) {
                    // Map the supabase rows back to the shape the component expects
                    const liveTrainings = tRes.data.map(row => ({
                        ...row.data,
                        id: row.slug
                    }));
                    setTrainings(liveTrainings);
                }
            } catch (err) {
                console.error("Error fetching trainings data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const lp = page.listingPage || fallbackTrainingsData.page.listingPage;

    if (loading) return null; // Prevent flash of fallback content while loading

    // If there are zero active trainings, we can either hide the section or show a message.
    if (trainings.length === 0) return null;

    return (
        <section className="trn-main">
            <style>{`
                .trn-main {
                    background-color: rgba(4, 0, 10, 0.70);
                    padding: 80px 0 100px;
                    position: relative;
                    overflow: hidden;
                }

                /* Section Header */
                .trn-main__header {
                    text-align: center;
                    margin-bottom: 60px;
                    position: relative;
                    z-index: 1;
                }
                .trn-main__eyebrow {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-family: var(--eventflow-font);
                    font-size: 12px; font-weight: 700;
                    letter-spacing: 3px; text-transform: uppercase;
                    color: var(--eventflow-base); margin-bottom: 16px;
                }
                .trn-main__eyebrow::before, .trn-main__eyebrow::after {
                    content: ''; display: inline-block;
                    width: 28px; height: 1px;
                    background: var(--eventflow-base); opacity: 0.5;
                }
                .trn-main__section-title {
                    font-family: var(--eventflow-font);
                    font-size: 36px; font-weight: 900;
                    color: #fff; margin: 0 0 16px;
                }
                .trn-main__section-sub {
                    font-family: var(--eventflow-font);
                    font-size: 15px; color: rgba(255,255,255,0.45);
                    max-width: 640px; margin: 0 auto; line-height: 1.7;
                }

                /* Training Cards Grid */
                .trn-main__grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
                    gap: 28px;
                    position: relative;
                    z-index: 1;
                }

                /* Training Card */
                .trn-card {
                    display: flex;
                    flex-direction: column;
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 20px;
                    overflow: hidden;
                    transition: all 0.3s ease;
                }
                .trn-card:hover {
                    transform: translateY(-4px);
                    border-color: rgba(255,255,255,0.15);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.4);
                }

                /* Card Image */
                .trn-card__img {
                    width: 100%;
                    height: 200px;
                    flex-shrink: 0;
                    background: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    position: relative;
                }
                .trn-card__img img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .trn-card__img span {
                    font-family: var(--eventflow-font);
                    font-size: 14px;
                    font-weight: 700;
                    color: #333;
                    text-align: center;
                    padding: 10px;
                }

                /* Card Content */
                .trn-card__content {
                    flex: 1;
                    padding: 24px 28px;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .trn-card__top-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .trn-card__type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-family: var(--eventflow-font);
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    color: var(--eventflow-base);
                    background: rgba(var(--eventflow-base-rgb),0.1);
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.25);
                }
                .trn-card__type-badge i { font-size: 9px; }
                .trn-card__duration {
                    font-family: 'Courier New', monospace;
                    font-size: 11px;
                    font-weight: 700;
                    color: rgba(255,255,255,0.4);
                    letter-spacing: 0.5px;
                    background: rgba(255,255,255,0.05);
                    padding: 4px 10px;
                    border-radius: 6px;
                }
                .trn-card__title {
                    font-family: var(--eventflow-font);
                    font-size: 20px;
                    font-weight: 800;
                    color: #fff;
                    margin: 0;
                    line-height: 1.3;
                }
                .trn-card__instructor {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-family: var(--eventflow-font);
                    font-size: 13px;
                    font-weight: 600;
                    color: rgba(255,255,255,0.6);
                }
                .trn-card__instructor i {
                    color: var(--eventflow-base);
                    font-size: 12px;
                }
                .trn-card__desc {
                    font-family: var(--eventflow-font);
                    font-size: 13px;
                    color: rgba(255,255,255,0.4);
                    line-height: 1.65;
                    margin: 0;
                }
                .trn-card__tags {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                    margin-top: 4px;
                }
                .trn-card__tag {
                    font-family: var(--eventflow-font);
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    padding: 4px 10px;
                    border-radius: 4px;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.08);
                    color: rgba(255,255,255,0.5);
                }
                .trn-card__tag--level {
                    border-color: transparent;
                }
                .trn-card__footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: auto;
                    padding-top: 12px;
                    border-top: 1px solid rgba(255,255,255,0.05);
                }
                .trn-card__seats {
                    font-family: var(--eventflow-font);
                    font-size: 12px;
                    color: rgba(255,255,255,0.35);
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .trn-card__seats i { font-size: 11px; color: var(--eventflow-base); }
                .trn-card__btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-family: var(--eventflow-font);
                    font-size: 12px;
                    font-weight: 700;
                    color: var(--eventflow-base);
                    text-decoration: none;
                    padding: 8px 18px;
                    border-radius: 50px;
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.3);
                    background: rgba(var(--eventflow-base-rgb),0.05);
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .trn-card__btn:hover {
                    background: var(--eventflow-base);
                    color: #fff;
                    border-color: var(--eventflow-base);
                    transform: translateY(-1px);
                }
                .trn-card__btn i { font-size: 10px; }

                /* CTA Section */
                .trn-main__cta {
                    margin-top: 60px;
                    background: rgba(var(--eventflow-base-rgb),0.05);
                    border: 1px dashed rgba(var(--eventflow-base-rgb),0.2);
                    border-radius: 16px;
                    padding: 36px 40px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    flex-wrap: wrap;
                    position: relative;
                    z-index: 1;
                }
                .trn-main__cta h4 {
                    font-family: var(--eventflow-font);
                    font-size: 20px;
                    font-weight: 800;
                    color: #fff;
                    margin: 0 0 6px;
                }
                .trn-main__cta p {
                    font-size: 14px;
                    color: rgba(255,255,255,0.4);
                    margin: 0;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .trn-main__grid {
                        grid-template-columns: 1fr;
                    }
                    .trn-card__img {
                        height: 180px;
                    }
                    .trn-card__content {
                        padding: 20px;
                    }
                    .trn-main__cta {
                        flex-direction: column;
                        padding: 24px 20px;
                        text-align: center;
                    }
                }
            `}</style>

            <div className="container">
                {/* Section Header */}
                <div className="trn-main__header wow fadeInUp" data-wow-delay="100ms">
                    <div className="trn-main__eyebrow">{page.sectionEyebrow}</div>
                    <h2 className="trn-main__section-title">{page.sectionTitle}</h2>
                    <p className="trn-main__section-sub">{page.sectionSubtitle}</p>
                </div>

                {/* Training Cards */}
                <div className="trn-main__grid">
                    {trainings.map((training, idx) => {
                        const levelColor = LEVEL_COLORS[training.level] || '#00e5ff';
                        return (
                            <div
                                key={training.id}
                                className="trn-card wow fadeInUp"
                                data-wow-delay={`${150 + idx * 80}ms`}
                            >
                                {/* Image */}
                                <div className="trn-card__img">
                                    {training.instructorImg && training.instructorImg.startsWith('http') ? (
                                        <img src={training.instructorImg} alt={training.title} />
                                    ) : (
                                        <span>{lp.imagePlaceholderText}</span>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="trn-card__content">
                                    <div className="trn-card__top-row">
                                        <span className="trn-card__type-badge">
                                            <i className="fa fa-chalkboard-teacher" />
                                            Training
                                        </span>
                                        <span className="trn-card__duration">{training.duration}</span>
                                    </div>

                                    <h3 className="trn-card__title">{training.title}</h3>

                                    <div className="trn-card__instructor">
                                        <i className="fa fa-user" />
                                        {training.instructor}
                                    </div>

                                    <p className="trn-card__desc">{training.description}</p>

                                    <div className="trn-card__tags">
                                        <span
                                            className="trn-card__tag trn-card__tag--level"
                                            style={{
                                                color: levelColor,
                                                background: `${levelColor}12`,
                                                borderColor: `${levelColor}30`,
                                                border: `1px solid ${levelColor}30`,
                                            }}
                                        >
                                            {training.level}
                                        </span>
                                        {training.tags && training.tags.map((tag, i) => (
                                            <span key={i} className="trn-card__tag">{tag}</span>
                                        ))}
                                    </div>

                                    <div className="trn-card__footer">
                                        {training.seats && (
                                            <span className="trn-card__seats">
                                                <i className="fa fa-chair" />
                                                {training.seats}
                                            </span>
                                        )}
                                        <Link
                                            to={`/trainings/${training.id}`}
                                            className="trn-card__btn"
                                        >
                                            {lp.cardButtonText}
                                            <i className="fa fa-arrow-right" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* CTA */}
                <div className="trn-main__cta wow fadeInUp" data-wow-delay="200ms">
                    <div>
                        <h4>{page.ctaTitle}</h4>
                        <p>{page.ctaSubtitle}</p>
                    </div>
                    <a href={page.ctaButtonLink} className="thm-btn">
                        {page.ctaButtonText} <span className="icon-arrow-right" />
                    </a>
                </div>
            </div>
        </section>
    );
}
